import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChefHat, Truck, Package, Clock, CheckCircle, AlertTriangle,
  BarChart3, Bot, LogOut, Menu, X, RefreshCw, PlayCircle,
  MapPin, Users, Timer, ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProduccionDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('kds');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [productionPlan, setProductionPlan] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [stats, setStats] = useState({
    pendingOrders: 0,
    inProduction: 0,
    ready: 0,
    delivered: 0
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/orders`, { headers: getAuthHeaders() });
      const data = await res.json();
      setOrders(data.slice(0, 20));
      
      // Calculate stats
      const pending = data.filter(o => o.status === 'pending').length;
      const inProd = data.filter(o => o.status === 'in_production').length;
      const ready = data.filter(o => o.status === 'ready').length;
      const delivered = data.filter(o => o.status === 'delivered').length;
      
      setStats({ pendingOrders: pending, inProduction: inProd, ready, delivered });
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      fetchOrders();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const generateProductionPlan = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/ops/production-plan`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date: new Date().toISOString().split('T')[0] })
      });
      const data = await res.json();
      setProductionPlan(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateRoutes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/ops/delivery-routes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date: new Date().toISOString().split('T')[0] })
      });
      const data = await res.json();
      setRoutes(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { id: 'kds', label: 'KDS', icon: ChefHat },
    { id: 'production', label: 'Plan Producción', icon: ClipboardCheck },
    { id: 'routes', label: 'Rutas Entrega', icon: Truck },
    { id: 'inventory', label: 'Alertas Stock', icon: Package },
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'confirmed': 'bg-blue-100 text-blue-700 border-blue-200',
      'in_production': 'bg-orange-100 text-orange-700 border-orange-200',
      'ready': 'bg-green-100 text-green-700 border-green-200',
      'delivered': 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pendiente',
      'confirmed': 'Confirmado',
      'in_production': 'En Producción',
      'ready': 'Listo',
      'delivered': 'Entregado'
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-stone-900 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-stone-800 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-stone-700">
          <div className="flex items-center justify-between">
            {sidebarOpen && <img src="/logo.png" alt="Lunch & Roll" className="h-8" />}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-stone-700 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-orange-600 text-white' 
                  : 'text-stone-300 hover:bg-stone-700'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-700">
          {sidebarOpen && (
            <div className="mb-4">
              <p className="text-stone-300 text-sm">{user?.name}</p>
              <p className="text-stone-400 text-xs">{user?.email}</p>
            </div>
          )}
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2 text-stone-300 hover:bg-stone-700 rounded-lg">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Salir</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-auto">
        {/* Header Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-3xl font-bold text-yellow-400">{stats.pendingOrders}</p>
                <p className="text-yellow-300 text-sm">Pendientes</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-500/20 border border-orange-500/30 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <ChefHat className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-3xl font-bold text-orange-400">{stats.inProduction}</p>
                <p className="text-orange-300 text-sm">En Producción</p>
              </div>
            </div>
          </div>
          <div className="bg-green-500/20 border border-green-500/30 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-3xl font-bold text-green-400">{stats.ready}</p>
                <p className="text-green-300 text-sm">Listos</p>
              </div>
            </div>
          </div>
          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <Truck className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-3xl font-bold text-emerald-400">{stats.delivered}</p>
                <p className="text-emerald-300 text-sm">Entregados</p>
              </div>
            </div>
          </div>
        </div>

        {/* KDS Tab - Kitchen Display System */}
        {activeTab === 'kds' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Kitchen Display System</h2>
              <Button onClick={fetchOrders} variant="outline" className="gap-2 border-stone-600 text-stone-300">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.filter(o => ['pending', 'confirmed', 'in_production'].includes(o.status)).map(order => (
                <div key={order.order_id} className="bg-stone-800 rounded-2xl p-4 border border-stone-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-mono font-bold">#{order.order_id?.slice(-6)}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {order.items?.slice(0, 3).map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-stone-300 text-sm">
                        <span>{item.quantity}x {item.name}</span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-stone-500 text-xs">+{order.items.length - 3} más</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {order.status === 'pending' && (
                      <Button 
                        size="sm" 
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => updateOrderStatus(order.order_id, 'in_production')}
                      >
                        <PlayCircle className="w-4 h-4 mr-1" />
                        Iniciar
                      </Button>
                    )}
                    {order.status === 'in_production' && (
                      <Button 
                        size="sm" 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => updateOrderStatus(order.order_id, 'ready')}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Listo
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {orders.filter(o => ['pending', 'confirmed', 'in_production'].includes(o.status)).length === 0 && (
              <div className="text-center py-12">
                <ChefHat className="w-16 h-16 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400">No hay órdenes pendientes</p>
              </div>
            )}
          </div>
        )}

        {/* Production Plan Tab */}
        {activeTab === 'production' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Bot className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Plan de Producción AI</h3>
                  <p className="text-orange-100">Genera un plan optimizado para hoy</p>
                </div>
              </div>
              <Button 
                onClick={generateProductionPlan}
                className="bg-white text-orange-700 hover:bg-orange-50"
                disabled={loading}
              >
                {loading ? 'Generando...' : 'Generar Plan'}
              </Button>
            </div>

            {productionPlan && (
              <div className="bg-stone-800 rounded-2xl p-6 border border-stone-700">
                <h4 className="font-semibold text-white mb-4">Plan del Día: {productionPlan.date}</h4>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-stone-700 p-4 rounded-xl">
                    <p className="text-stone-400 text-sm">Órdenes a producir</p>
                    <p className="text-2xl font-bold text-white">{productionPlan.total_orders}</p>
                  </div>
                  <div className="bg-stone-700 p-4 rounded-xl">
                    <p className="text-stone-400 text-sm">Items únicos</p>
                    <p className="text-2xl font-bold text-white">{Object.keys(productionPlan.production_items || {}).length}</p>
                  </div>
                </div>
                <div className="bg-stone-900 rounded-xl p-4">
                  <pre className="text-sm text-stone-300 whitespace-pre-wrap overflow-auto max-h-96">
                    {productionPlan.ai_production_plan}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Routes Tab */}
        {activeTab === 'routes' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Optimización de Rutas AI</h3>
                  <p className="text-blue-100">Rutas de entrega optimizadas para hoy</p>
                </div>
              </div>
              <Button 
                onClick={generateRoutes}
                className="bg-white text-blue-700 hover:bg-blue-50"
                disabled={loading}
              >
                {loading ? 'Optimizando...' : 'Generar Rutas'}
              </Button>
            </div>

            {routes && (
              <div className="bg-stone-800 rounded-2xl p-6 border border-stone-700">
                <h4 className="font-semibold text-white mb-4">Rutas del Día</h4>
                <div className="bg-stone-700 p-4 rounded-xl mb-6">
                  <p className="text-stone-400 text-sm">Total entregas</p>
                  <p className="text-2xl font-bold text-white">{routes.total_deliveries}</p>
                </div>
                <div className="bg-stone-900 rounded-xl p-4">
                  <pre className="text-sm text-stone-300 whitespace-pre-wrap overflow-auto max-h-96">
                    {routes.ai_routes}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="bg-stone-800 rounded-2xl p-8 text-center border border-stone-700">
            <AlertTriangle className="w-16 h-16 text-stone-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Alertas de Inventario</h3>
            <p className="text-stone-400">Conectado con el dashboard de Compras</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProduccionDashboard;
